import React, { useEffect, useState } from "react";
import { Tag, X, ArrowRight, Lightning, MonitorPlay, Buildings, Globe } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PROMO_CODE = "SAVE25";
const PROMO_AMOUNT = "$25";
const STORAGE_KEY = "tcllc_promo_seen";

// Rotating messages — index 0 is the keep-the-discount in the popup,
// the banner cycles through all of them every 5 seconds.
const ROTATING_MESSAGES = [
  { icon: Buildings,   text: "Free Business IT Assessment",            target: "assessment" },
  { icon: Lightning,   text: "Same-Day Computer Repair Available",     target: "book" },
  { icon: MonitorPlay, text: "Remote Support Available Nationwide",    target: "book" },
  { icon: Globe,       text: "Managed IT Plans Starting at $49/User",  target: "managed-it" },
];

const ROTATE_INTERVAL_MS = 5000;

export default function Promo({ onClaim, onClose }) {
  const [barOpen, setBarOpen] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);

  const closeBar = () => {
    setBarOpen(false);
    if (onClose) onClose();
  };

  // Rotate banner messages
  useEffect(() => {
    if (!barOpen) return;
    const t = setInterval(() => {
      setMsgIndex((i) => (i + 1) % ROTATING_MESSAGES.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [barOpen]);

  // Show dialog once per session after 20s
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => {
      setDialogOpen(true);
      sessionStorage.setItem(STORAGE_KEY, "1");
    }, 20000);
    return () => clearTimeout(t);
  }, []);

  const claim = () => {
    setDialogOpen(false);
    if (onClaim) onClaim(PROMO_CODE);
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleBannerClick = () => {
    const m = ROTATING_MESSAGES[msgIndex];
    let id = null;
    if (m.target === "assessment") id = "business-assessment";
    else if (m.target === "managed-it") id = "managed-it";
    else id = "contact";
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const current = ROTATING_MESSAGES[msgIndex];
  const Icon = current.icon;

  return (
    <>
      {barOpen && (
        <div
          data-testid="promo-banner"
          className="fixed top-0 inset-x-0 z-[60] bg-blue-600 text-white text-sm"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between gap-3">
            <button
              onClick={handleBannerClick}
              data-testid="promo-banner-claim"
              className="flex items-center gap-2 hover:gap-3 transition-all font-medium truncate min-w-0"
            >
              <Icon size={14} weight="fill" />
              <span
                key={msgIndex}
                className="truncate animate-fade-up"
                style={{ animationDuration: "0.6s" }}
                data-testid={`promo-msg-${msgIndex}`}
              >
                {current.text}
              </span>
              <ArrowRight size={14} weight="bold" className="hidden sm:inline shrink-0" />
            </button>
            <button
              onClick={closeBar}
              data-testid="promo-banner-close"
              aria-label="Close promo"
              className="p-1 -mr-1 text-white/80 hover:text-white shrink-0"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="promo-dialog" className="sm:max-w-md p-0 overflow-hidden border-zinc-200">
          <div className="bg-zinc-950 text-white p-8 relative overflow-hidden">
            <div className="absolute inset-0 hero-grid opacity-40" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-600/20 border border-blue-500/30 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-blue-300">
                <Tag size={11} weight="fill" /> Limited time
              </div>
              <DialogTitle className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">
                Get <span className="text-blue-500">{PROMO_AMOUNT} off</span> your first service.
              </DialogTitle>
              <DialogDescription className="mt-2 text-zinc-300 text-base">
                New customer? Claim your discount — apply it to any tune-up, virus removal, or data backup.
              </DialogDescription>
            </div>
          </div>

          <div className="p-6 sm:p-8 bg-white">
            <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center">
              <div className="text-xs text-zinc-500 uppercase tracking-widest">Your code</div>
              <div className="mt-1 font-mono text-2xl font-extrabold tracking-[0.25em] text-blue-700">{PROMO_CODE}</div>
            </div>
            <button
              onClick={claim}
              data-testid="promo-dialog-claim"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-11 font-semibold blue-glow transition-all"
            >
              Claim my {PROMO_AMOUNT} off <ArrowRight size={16} weight="bold" />
            </button>
            <button
              onClick={() => setDialogOpen(false)}
              data-testid="promo-dialog-dismiss"
              className="mt-3 w-full text-xs text-zinc-500 hover:text-zinc-800"
            >
              No thanks, I'll pay full price
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
